import { defineStore } from 'pinia';

// Session state machine for the marketplace Messages (Phase 5). Ported from the
// marketplace homeowner reference (mkp-frontend `cometchat-provider`), but driven by
// the tenant's `cometchatAuthToken` (app-boot payload) instead of a Supabase profile.
//
//   loading      → init + login in flight
//   ready        → logged in; conversation list / chat can mount
//   unavailable  → no token (backend hasn't provisioned the tenant's CometChat user
//                  yet — i.e. no demand received). Drives the "no inbox yet" empty state.
//   error        → init/login failed; the page offers a retry
//
// Init is lazy and page-scoped (the Messages page calls `setup()` on mount): the
// heavy CometChat SDK stays out of every other route's bundle, and an init failure
// can never affect unrelated pages.
export type CometChatStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export const useCometchatStore = defineStore('cometchat', () => {
    const status = ref<CometChatStatus>('idle');
    const error = ref<Error | null>(null);
    const currentUserUid = ref<string | null>(null);

    // Token we last completed a successful login for — lets `setup()` short-circuit
    // when re-entered with the same identity (tab focus, route re-mount).
    let activeToken: string | null = null;
    // Monotonic; each `setup()` snapshots it and bails when a newer run supersedes it
    // (tenant switch / retry firing while a previous run is still awaiting).
    let setupRun = 0;

    const isReady = computed<boolean>(() => status.value === 'ready');

    const setup = async (): Promise<void> => {
        const token = useAuthStore().tenant?.cometchatAuthToken ?? null;
        const runId = ++setupRun;
        const isCurrent = (): boolean => setupRun === runId;

        // No token → nothing to log into. Surface the empty state.
        if (!token) {
            activeToken = null;
            currentUserUid.value = null;
            error.value = null;
            status.value = 'unavailable';
            return;
        }

        // Same identity already live — don't flip the UI back to a spinner.
        if (token === activeToken && status.value === 'ready') return;

        status.value = 'loading';
        error.value = null;

        try {
            const { cometchatAppId, cometchatRegion } = useRuntimeConfig().public;
            const { initCometChat, loginCometChatWithAuthToken, getLoggedInUserUid, setCometChatLocale } = await import('~~/app/marketplace/messages/cometchat-init');

            // Localize the UI-Kit (date separators / built-in labels) to the app's current locale BEFORE
            // init — the UI-Kit reads `CometChatLocalize` when it builds its formatters, so setting it after
            // init leaves the date separator in the default English ("11 Jun, 2026" vs "11 juin 2026").
            setCometChatLocale(useTranslation().getCurrentLocale());

            await initCometChat(cometchatAppId, cometchatRegion);
            if (!isCurrent()) return;

            await loginCometChatWithAuthToken(token);
            if (!isCurrent()) return;

            const uid = await getLoggedInUserUid();
            if (!isCurrent()) return;

            activeToken = token;
            currentUserUid.value = uid;
            status.value = 'ready';
        } catch (err) {
            const { normalizeCometChatError } = await import('~~/app/marketplace/messages/cometchat-init');
            const normalized = normalizeCometChatError(err);
            console.error('[CometChat] Setup failed', normalized);
            if (!isCurrent()) return;
            activeToken = null;
            error.value = normalized;
            status.value = 'error';
        }
    };

    // Recovery path for a stuck 401 — force a real logout so the next login skips the
    // optimistic short-circuit, then re-run setup.
    const retry = async (): Promise<void> => {
        const { logoutCometChat } = await import('~~/app/marketplace/messages/cometchat-init');
        await logoutCometChat();
        activeToken = null;
        await setup();
    };

    // Drop the session (tenant switch / sign-out). Cancels any in-flight setup.
    const logout = async (): Promise<void> => {
        setupRun++;
        const { logoutCometChat } = await import('~~/app/marketplace/messages/cometchat-init');
        await logoutCometChat();
        activeToken = null;
        currentUserUid.value = null;
        error.value = null;
        status.value = 'idle';
    };

    return { status, error, currentUserUid, isReady, setup, retry, logout };
});
